import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Email,
  Person,
  Refresh
} from '@mui/icons-material';
import { recipientAPI, userAPI } from '../services/api';

interface Recipient {
  id: string;
  email: string;
  name: string | null;
  is_active: boolean;
  created_at: string;
}

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const Settings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Recipients state
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [recipientDialog, setRecipientDialog] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    data: Partial<Recipient>;
  }>({ open: false, mode: 'create', data: {} });

  // Users state
  const [users, setUsers] = useState<User[]>([]);
  const [userDialog, setUserDialog] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    data: Partial<User> & { password?: string };
  }>({ open: false, mode: 'create', data: {} });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [recipientsData, usersData] = await Promise.all([
        recipientAPI.getAll(showInactive),
        userAPI.getAll()
      ]);
      setRecipients(recipientsData);
      setUsers(usersData);
    } catch (err: any) {
      setError('Failed to load settings data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    loadData();
  }, [showInactive, loadData]);

  // Recipient handlers
  const handleSaveRecipient = async () => {
    try {
      if (recipientDialog.mode === 'create') {
        await recipientAPI.create({
          email: recipientDialog.data.email!,
          name: recipientDialog.data.name || undefined
        });
        setSuccess('Recipient added successfully');
      } else {
        await recipientAPI.update(recipientDialog.data.id!, {
          email: recipientDialog.data.email,
          name: recipientDialog.data.name || undefined,
          is_active: recipientDialog.data.is_active
        });
        setSuccess('Recipient updated successfully');
      }
      setRecipientDialog({ open: false, mode: 'create', data: {} });
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save recipient');
    }
  };

  const handleDeleteRecipient = async (id: string) => {
    if (!window.confirm('Are you sure you want to deactivate this recipient?')) return;
    try {
      await recipientAPI.delete(id);
      setSuccess('Recipient deactivated successfully');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete recipient');
    }
  };

  // User handlers
  const handleSaveUser = async () => {
    try {
      if (userDialog.mode === 'create') {
        await userAPI.create({
          email: userDialog.data.email!,
          password: userDialog.data.password!,
          first_name: userDialog.data.first_name!,
          last_name: userDialog.data.last_name!,
          role: userDialog.data.role!
        });
        setSuccess('User created successfully');
      } else {
        await userAPI.update(userDialog.data.id!, {
          email: userDialog.data.email,
          first_name: userDialog.data.first_name,
          last_name: userDialog.data.last_name,
          role: userDialog.data.role,
          is_active: userDialog.data.is_active
        });
        setSuccess('User updated successfully');
      }
      setUserDialog({ open: false, mode: 'create', data: {} });
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save user');
    }
  };

  const handleDeactivateUser = async (id: string) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    try {
      await userAPI.deactivate(id);
      setSuccess('User deactivated successfully');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to deactivate user');
    }
  };

  const getRoleColor = (role: string): 'primary' | 'secondary' | 'default' => {
    switch (role) {
      case 'locate_admin': return 'primary';
      case 'locate_viewer': return 'secondary';
      case 'novus_user': return 'default';
      default: return 'default';
    }
  };

  const formatRole = (role: string): string => {
    switch (role) {
      case 'locate_admin': return 'Admin';
      case 'locate_viewer': return 'Viewer';
      case 'novus_user': return 'Novus User';
      default: return role;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage Novus Capital recipients and platform users
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Card>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<Email />} label="Novus Recipients" iconPosition="start" />
          <Tab icon={<Person />} label="Platform Users" iconPosition="start" />
        </Tabs>

        {/* Novus Recipients Tab */}
        <TabPanel value={tabValue} index={0}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6">Email Recipients</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showInactive}
                      onChange={(e) => setShowInactive(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Show inactive"
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button startIcon={<Refresh />} onClick={loadData} variant="outlined" size="small">
                  Refresh
                </Button>
                <Button
                  startIcon={<Add />}
                  variant="contained"
                  onClick={() => setRecipientDialog({ open: true, mode: 'create', data: {} })}
                >
                  Add Recipient
                </Button>
              </Box>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These email addresses will receive all ATM notice notifications (activation, completion, cancellation, etc.)
            </Typography>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recipients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No recipients configured</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    recipients.map((recipient) => (
                      <TableRow key={recipient.id}>
                        <TableCell>{recipient.email}</TableCell>
                        <TableCell>{recipient.name || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={recipient.is_active ? 'Active' : 'Inactive'}
                            color={recipient.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => setRecipientDialog({
                              open: true,
                              mode: 'edit',
                              data: recipient
                            })}
                          >
                            <Edit />
                          </IconButton>
                          {recipient.is_active && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteRecipient(recipient.id)}
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </TabPanel>

        {/* Platform Users Tab */}
        <TabPanel value={tabValue} index={1}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Platform Users</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button startIcon={<Refresh />} onClick={loadData} variant="outlined" size="small">
                  Refresh
                </Button>
                <Button
                  startIcon={<Add />}
                  variant="contained"
                  onClick={() => setUserDialog({ open: true, mode: 'create', data: { role: 'locate_viewer' } })}
                >
                  Add User
                </Button>
              </Box>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No users found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.first_name} {user.last_name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={formatRole(user.role)}
                            color={getRoleColor(user.role)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.is_active ? 'Active' : 'Inactive'}
                            color={user.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => setUserDialog({
                              open: true,
                              mode: 'edit',
                              data: user
                            })}
                          >
                            <Edit />
                          </IconButton>
                          {user.is_active && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeactivateUser(user.id)}
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </TabPanel>
      </Card>

      {/* Recipient Dialog */}
      <Dialog open={recipientDialog.open} onClose={() => setRecipientDialog({ open: false, mode: 'create', data: {} })}>
        <DialogTitle>
          {recipientDialog.mode === 'create' ? 'Add Novus Recipient' : 'Edit Recipient'}
        </DialogTitle>
        <DialogContent sx={{ minWidth: 400 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            required
            value={recipientDialog.data.email || ''}
            onChange={(e) => setRecipientDialog(prev => ({
              ...prev,
              data: { ...prev.data, email: e.target.value }
            }))}
          />
          <TextField
            margin="dense"
            label="Name (Optional)"
            fullWidth
            value={recipientDialog.data.name || ''}
            onChange={(e) => setRecipientDialog(prev => ({
              ...prev,
              data: { ...prev.data, name: e.target.value }
            }))}
          />
          {recipientDialog.mode === 'edit' && (
            <FormControlLabel
              control={
                <Switch
                  checked={recipientDialog.data.is_active ?? true}
                  onChange={(e) => setRecipientDialog(prev => ({
                    ...prev,
                    data: { ...prev.data, is_active: e.target.checked }
                  }))}
                />
              }
              label="Active"
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecipientDialog({ open: false, mode: 'create', data: {} })}>
            Cancel
          </Button>
          <Button onClick={handleSaveRecipient} variant="contained" disabled={!recipientDialog.data.email}>
            {recipientDialog.mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Dialog */}
      <Dialog open={userDialog.open} onClose={() => setUserDialog({ open: false, mode: 'create', data: {} })}>
        <DialogTitle>
          {userDialog.mode === 'create' ? 'Add Platform User' : 'Edit User'}
        </DialogTitle>
        <DialogContent sx={{ minWidth: 400 }}>
          <TextField
            autoFocus
            margin="dense"
            label="First Name"
            fullWidth
            required
            value={userDialog.data.first_name || ''}
            onChange={(e) => setUserDialog(prev => ({
              ...prev,
              data: { ...prev.data, first_name: e.target.value }
            }))}
          />
          <TextField
            margin="dense"
            label="Last Name"
            fullWidth
            required
            value={userDialog.data.last_name || ''}
            onChange={(e) => setUserDialog(prev => ({
              ...prev,
              data: { ...prev.data, last_name: e.target.value }
            }))}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            required
            value={userDialog.data.email || ''}
            onChange={(e) => setUserDialog(prev => ({
              ...prev,
              data: { ...prev.data, email: e.target.value }
            }))}
          />
          {userDialog.mode === 'create' && (
            <TextField
              margin="dense"
              label="Password"
              type="password"
              fullWidth
              required
              value={userDialog.data.password || ''}
              onChange={(e) => setUserDialog(prev => ({
                ...prev,
                data: { ...prev.data, password: e.target.value }
              }))}
              helperText="Minimum 8 characters"
            />
          )}
          <FormControl fullWidth margin="dense">
            <InputLabel>Role</InputLabel>
            <Select
              value={userDialog.data.role || 'locate_viewer'}
              label="Role"
              onChange={(e) => setUserDialog(prev => ({
                ...prev,
                data: { ...prev.data, role: e.target.value }
              }))}
            >
              <MenuItem value="locate_admin">Admin (Full Access)</MenuItem>
              <MenuItem value="locate_viewer">Viewer (Read Only)</MenuItem>
              <MenuItem value="novus_user">Novus User</MenuItem>
            </Select>
          </FormControl>
          {userDialog.mode === 'edit' && (
            <FormControlLabel
              control={
                <Switch
                  checked={userDialog.data.is_active ?? true}
                  onChange={(e) => setUserDialog(prev => ({
                    ...prev,
                    data: { ...prev.data, is_active: e.target.checked }
                  }))}
                />
              }
              label="Active"
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialog({ open: false, mode: 'create', data: {} })}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveUser} 
            variant="contained"
            disabled={
              !userDialog.data.email || 
              !userDialog.data.first_name || 
              !userDialog.data.last_name ||
              (userDialog.mode === 'create' && !userDialog.data.password)
            }
          >
            {userDialog.mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Settings;

